import { z } from 'zod';
import { createDateTimeSchema, dateTimeUtcStringSchema } from '@mews/framework';
import { GatewayPaymentMethod, PaymentRequestReason } from '@mews-commander/data-access';
import { CommonProcessPaymentFormSchema, ProcessPaymentFormField } from '../ProcessPaymentFormSchema';

export enum PaymentRequestFormField {
    Reason = 'reason',
    Description = 'description',
    ExpirationUtc = 'expirationUtc',
    Type = 'type',
    Email = 'email',
    Methods = 'methods',
}

// TODO: replace with PaymentRequestType DTO once backend is updated
export enum PaymentRequestType {
    Payment = 'Payment',
    Preauthorization = 'Preauthorization',
    PaymentMethod = 'PaymentMethod',
}

const paymentRequestFormBaseSchema = CommonProcessPaymentFormSchema.extend({
    [PaymentRequestFormField.Description]: z.string().trim().min(1),
    [PaymentRequestFormField.ExpirationUtc]: dateTimeUtcStringSchema,
    [PaymentRequestFormField.Type]: z.nativeEnum(PaymentRequestType).default(PaymentRequestType.Payment),
}).extend<{}>(createDateTimeSchema({ fieldName: 'expirationUtc', required: true }));

const paymentMethodsFieldSchema = z.array(z.nativeEnum(GatewayPaymentMethod)).min(1);

export const createPaymentRequestFormSchema = (isPaymentRequestCollectTypeEnabled: boolean) => {
    const paymentRequestTypePaymentBaseSchema = paymentRequestFormBaseSchema.extend({
        [PaymentRequestFormField.Type]: z.literal(PaymentRequestType.Payment),
        [PaymentRequestFormField.Reason]: z.nativeEnum(PaymentRequestReason),
    });

    const paymentRequestTypePaymentSchema = isPaymentRequestCollectTypeEnabled
        ? paymentRequestTypePaymentBaseSchema.extend({
              [PaymentRequestFormField.Email]: z.string().email(),
              [PaymentRequestFormField.Methods]: paymentMethodsFieldSchema,
          })
        : paymentRequestTypePaymentBaseSchema;

    const paymentRequestTypePaymentMethodSchema = paymentRequestFormBaseSchema
        .omit({ [ProcessPaymentFormField.Amount]: true })
        .extend({
            [PaymentRequestFormField.Type]: z.literal(PaymentRequestType.PaymentMethod),
            [PaymentRequestFormField.Methods]: paymentMethodsFieldSchema,
            [PaymentRequestFormField.Email]: z.string().email(),
        });

    const paymentRequestTypePreauthorizationSchema = paymentRequestFormBaseSchema.extend({
        [PaymentRequestFormField.Type]: z.literal(PaymentRequestType.Preauthorization),
        [PaymentRequestFormField.Email]: z.string().email(),
    });

    return z.discriminatedUnion(PaymentRequestFormField.Type, [
        paymentRequestTypePaymentSchema,
        paymentRequestTypePaymentMethodSchema,
        paymentRequestTypePreauthorizationSchema,
    ]);
};

export type PaymentRequestFormValues = z.infer<ReturnType<typeof createPaymentRequestFormSchema>>;

export type PaymentRequestTypePaymentFormValues = Extract<
    PaymentRequestFormValues,
    { [PaymentRequestFormField.Type]: PaymentRequestType.Payment }
> & {
    // TODO: remove when paymentRequestCollectTypeEnabled FF is removed
    [PaymentRequestFormField.Methods]?: GatewayPaymentMethod[];
    [PaymentRequestFormField.Email]?: string;
};

export type PaymentRequestTypePreauthorizationFormValues = Extract<
    PaymentRequestFormValues,
    { [PaymentRequestFormField.Type]: PaymentRequestType.Preauthorization }
>;
